/**
 * Cmd-K spotlight: navigate, search the catalog, search bottles in cellar,
 * open scan modes. Catalog search hits the merged endpoint that includes
 * Vinmonopolet, LWIN, OpenFoodFacts, X-Wines, curated and CatalogBeer rows
 * — so wines, beers and spirits all show up here.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bottles, Catalog } from '@/lib/api';
import { Icon, type IconName } from '@/design-system';
import { useDebounce } from '@/lib/hooks';
import { clsx } from 'clsx';

interface Action {
  id: string;
  title: string;
  hint?: string;
  icon: IconName;
  to: string;
  group: 'gå til' | 'kjeller' | 'katalog' | 'verktøy';
}

const STATIC_ACTIONS: Action[] = [
  { id: 'go-cellar', title: 'Kjelleren', icon: 'bottle', to: '/cellar', group: 'gå til' },
  { id: 'go-scan', title: 'Skann', icon: 'scan', to: '/scan', group: 'gå til' },
  { id: 'go-racks', title: 'Hyller', icon: 'rack', to: '/racks', group: 'gå til' },
  { id: 'go-occasions', title: 'Anledninger', icon: 'utensils', to: '/occasions', group: 'gå til' },
  { id: 'go-pair', title: 'Mat-paring', icon: 'chef-hat', to: '/pair', group: 'gå til' },
  { id: 'go-recommend', title: 'Anbefalinger', icon: 'sparkles', to: '/recommend', group: 'gå til' },
  { id: 'go-labels', title: 'Etiketter', icon: 'tag', to: '/labels', group: 'gå til' },
  { id: 'go-scans', title: 'Skanne-historikk', icon: 'clock', to: '/scans', group: 'verktøy' },
  { id: 'go-admin', title: 'Husholdning & medlemmer', icon: 'users', to: '/admin', group: 'verktøy' },
  { id: 'go-settings', title: 'Innstillinger', icon: 'settings', to: '/settings', group: 'verktøy' },
];

interface CommandBarProps {
  open: boolean;
  onClose: () => void;
}

export function CommandBar({ open, onClose }: CommandBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const debounced = useDebounce(query, 220);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setCursor(0);
    }
  }, [open]);

  const bottlesQ = useQuery({
    queryKey: ['cmd.bottles', debounced],
    queryFn: () => Bottles.list({ q: debounced, status: 'cellared', limit: 6 }),
    enabled: open && debounced.length >= 2,
  });

  // Catalog search — wines, beers, spirits across Vinmonopolet, LWIN,
  // OpenFoodFacts, X-Wines, curated and CatalogBeer (whatever's wired).
  const catalogQ = useQuery({
    queryKey: ['cmd.catalog', debounced],
    queryFn: () => Catalog.search(debounced, 8),
    enabled: open && debounced.length >= 2,
    staleTime: 5 * 60_000,
  });

  const lower = query.toLowerCase();
  const filtered = useMemo(() => STATIC_ACTIONS.filter((a) => a.title.toLowerCase().includes(lower)), [lower]);

  const bottleActions: Action[] = (bottlesQ.data?.bottles ?? []).map((b) => ({
    id: `bottle-${b.id}`,
    title: b.name,
    hint: [b.producer, b.vintage, b.region].filter(Boolean).join(' · '),
    icon: 'bottle' as const,
    to: `/bottle/${b.id}`,
    group: 'kjeller' as const,
  }));

  const catalogActions: Action[] = (catalogQ.data?.products ?? []).map((p) => {
    const name = p.basic?.productLongName ?? p.basic?.productShortName ?? p.basic?.productId ?? '';
    const country = (p.origins as { country?: string } | undefined)?.country;
    const region = (p.origins as { region?: string } | undefined)?.region;
    const hint = [country, region, p.basic?.vintage].filter(Boolean).join(' · ');
    return {
      id: `catalog-${p.basic?.productId ?? name}`,
      title: name,
      hint,
      icon: 'bottle' as const,
      to: `/bottle/${encodeURIComponent(p.basic?.productId ?? '')}`,
      group: 'katalog' as const,
    };
  });

  const items = [...filtered, ...bottleActions, ...catalogActions];

  useEffect(() => {
    setCursor(0);
  }, [items.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(items.length - 1, c + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === 'Enter') {
        const item = items[cursor];
        if (item) {
          e.preventDefault();
          navigate(item.to);
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, cursor, items, navigate, onClose]);

  if (!open) return null;

  // group rendering
  const groups = ['gå til', 'kjeller', 'katalog', 'verktøy'] as const;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Søk og naviger"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh] backdrop-blur"
      style={{ background: 'var(--ui-modal-scrim)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="ui-card w-full max-w-xl overflow-hidden p-0">
        <div className="flex items-center gap-3 border-b border-line px-4 py-3">
          <Icon name="search" size={16} className="text-mist" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk i kjeller, naviger..."
            className="flex-1 bg-transparent text-base text-parchment outline-none placeholder:text-mist"
          />
          <kbd className="hidden text-[10px] text-mist sm:inline">ESC</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {items.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-mist">Ingen treff.</div>
          )}
          {groups.map((g) => {
            const groupItems = items.filter((it) => it.group === g);
            if (!groupItems.length) return null;
            return (
              <div key={g} className="grid gap-0.5 py-1">
                <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-mist">{g}</div>
                {groupItems.map((it) => {
                  const idx = items.indexOf(it);
                  const active = idx === cursor;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onMouseEnter={() => setCursor(idx)}
                      onClick={() => {
                        navigate(it.to);
                        onClose();
                      }}
                      className={clsx(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                        active ? 'bg-candle/10 text-candle' : 'text-parchment hover:bg-overlay-soft',
                      )}
                    >
                      <Icon name={it.icon} size={16} className={active ? 'text-candle' : 'text-mist'} />
                      <div className="grid flex-1">
                        <span className="truncate">{it.title}</span>
                        {it.hint && <span className="truncate text-xs text-mist">{it.hint}</span>}
                      </div>
                      {active && <Icon name="arrow-right" size={14} />}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
